import React, { useEffect, useRef, useState, useContext } from "react";

import edit from "../../../../assets/images/homepage/setting/general/edit.svg";
import editDark from "../../../../assets/images/homepage/setting/general/editDark.svg";
import useTheme from "hooks/observer";

import "./general.scss";
import Button from "components/Button/index";
import Email from "components/Settings/Email/index";
import DeleteAccount from "components/Settings/DeleteAccount/index";
import { ManageUser as MU } from "@induz/sdk";
import { useToast } from "components/Toaster/index";
import { signInContext } from "context/SigninContext";
import { createProfileThumbnail } from "utils/imageThumbnail";

function EmployeeGeneral({ setEditProfile, editProfile, onProfileUpdated }) {
  const theme = useTheme();
  const uploadRef = useRef();
  const mailInitial =
    ((sessionStorage.getItem("mail") || "@").trim().charAt(0).toUpperCase()) || "@";

  const imageMimeTypes = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    gif: "image/gif",
    bmp: "image/bmp",
    svg: "image/svg+xml",
  };
  const [companyName, setCompanyName] = useState("");
  // const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [profilePic, setprofilePic] = useState(null);
  const [profilePicName, setProfilePicName] = useState(null);
  const [imageArray, setImageArray] = useState(null);
  const [existingProfilePicBytes, setExistingProfilePicBytes] = useState(null);
  const [profilePicRemoved, setProfilePicRemoved] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");

  const getFileExtension = (filename) => {
    const match = filename.match(/\.(\w+)$/);
    return match ? match[1].toLowerCase() : null;
  };

  const getImageMimeType = (file) => {
    if (file?.type && file.type.startsWith("image/")) return file.type;
    const ext = getFileExtension(file?.name || "");
    return (ext && imageMimeTypes[ext]) || "image/png";
  };

  const readFileToUint8Array = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        resolve(new Uint8Array(arrayBuffer));
      };
      reader.onerror = () => reject(reader.error || new Error("Failed to read image"));
      reader.readAsArrayBuffer(file);
    });

  const resizeImageFile = (file, maxSize = 512) =>
    new Promise((resolve, reject) => {
      const imageUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = async () => {
        try {
          const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
          const targetWidth = Math.max(1, Math.round(img.width * scale));
          const targetHeight = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            URL.revokeObjectURL(imageUrl);
            reject(new Error("Canvas not supported"));
            return;
          }
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          const mimeType = getImageMimeType(file);
          canvas.toBlob(async (blob) => {
            URL.revokeObjectURL(imageUrl);
            if (!blob) {
              reject(new Error("Failed to compress image"));
              return;
            }
            const buffer = await blob.arrayBuffer();
            resolve({ blob, bytes: new Uint8Array(buffer) });
          }, mimeType, 0.92);
        } catch (err) {
          URL.revokeObjectURL(imageUrl);
          reject(err);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Failed to load image"));
      };
      img.src = imageUrl;
    });

  const detectImageMimeType = (bytes) => {
    if (!bytes || bytes.length < 4) return "image/png";
    const signature = Array.from(bytes.slice(0, 12));
    if (signature[0] === 0x89 && signature[1] === 0x50 && signature[2] === 0x4e && signature[3] === 0x47) {
      return "image/png";
    }
    if (signature[0] === 0xff && signature[1] === 0xd8 && signature[2] === 0xff) {
      return "image/jpeg";
    }
    if (signature[0] === 0x47 && signature[1] === 0x49 && signature[2] === 0x46) {
      return "image/gif";
    }
    if (signature[0] === 0x42 && signature[1] === 0x4d) {
      return "image/bmp";
    }
    if (
      signature[0] === 0x52 && signature[1] === 0x49 && signature[2] === 0x46 && signature[3] === 0x46 &&
      signature[8] === 0x57 && signature[9] === 0x45 && signature[10] === 0x42 && signature[11] === 0x50
    ) {
      return "image/webp";
    }
    const asciiHead = String.fromCharCode(...bytes.slice(0, 200)).toLowerCase();
    if (asciiHead.includes("<svg")) {
      return "image/svg+xml";
    }
    return "image/png";
  };

  const isImageFile = (filename) => {
    const ext = getFileExtension(filename);
    return ext && Object.prototype.hasOwnProperty.call(imageMimeTypes, ext);
  };

  // const getMimeType = (filename) => {
  //   const ext = getFileExtension(filename);
  //   return imageMimeTypes[ext] || "application/octet-stream";
  // };
  // Image upload
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicRemoved(false);
      if (file.size > MAX_FILE_SIZE) {
        showToast("File size must be less than 5MB", "error");
        event.target.value = "";
        return;
      }
      if (!isImageFile(file.name)) {
        console.warn("Unsupported file format:", file.name);
        return;
      }
      const mimeType = getImageMimeType(file);
      setProfilePicName(file.name);

      const isNonResizable = mimeType === "image/gif" || mimeType === "image/svg+xml";
      if (isNonResizable) {
        readFileToUint8Array(file)
          .then((bytes) => {
            setImageArray(bytes);
            setprofilePic(URL.createObjectURL(file));
          })
          .catch((err) => {
            console.error("Failed to read image:", err);
          });
        return;
      }

      createProfileThumbnail(file)
        .then(({ blob, bytes }) => {
          setImageArray(bytes);
          setprofilePic(URL.createObjectURL(blob));
        })
        .catch(async (err) => {
          console.error("Failed to create profile thumbnail:", err);
          try {
            const bytes = await readFileToUint8Array(file);
            setImageArray(bytes);
            setprofilePic(URL.createObjectURL(file));
          } catch (readErr) {
            console.error("Failed to read image:", readErr);
          }
        });
    }
  };

  const uploadHandler = () => {
    uploadRef.current.click();
  };

  const handleEdit = () => {
    setEditProfile(true);
  };

  const handleCancel = () => {
    setEditProfile(false);
    getUserDetails();
  };
  const showToast = useToast();
  const { userMail } = useContext(signInContext);

  const getUserDetails = async (forceRefresh = false) => {

    try {
      const user = await MU.getUserDetails(userMail, forceRefresh);

      const firstName = user.getFirstName();

      setFirstName(firstName || "");
      const lastName = user.getLastName();

      setLastName(lastName || "");
      // companyName will be updated automatically by useEffect when firstName/lastName change
      const phone = user.getPhone();

      setPhoneNumber(phone || "");
      const address = user.getAddress();

      setAddress(address || "");
      const picBytes = user.getProfilePicture?.();
      if (picBytes && picBytes.length > 0) {
        setExistingProfilePicBytes(picBytes);
        const base64Pic = btoa(String.fromCharCode(...picBytes));
        const mimeType = detectImageMimeType(picBytes);
        setprofilePic(`data:${mimeType};base64,${base64Pic}`);
      } else {
        setExistingProfilePicBytes(null);
      }
      setProfilePicRemoved(false);
    } catch (error) {
      showToast("Failed to get user details", "error");
      console.error("Failed to get user details:", error);
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  // Update companyName when firstName or lastName changes
  useEffect(() => {
    const fullName = [firstName || "", lastName || ""].filter(Boolean).join(" ").trim();
    setCompanyName(fullName || "");
  }, [firstName, lastName]);
  const [loading, setLoading] = useState(false);
  const canSaveProfile =
    firstName.trim() !== "" && lastName.trim() !== "";
  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      return;
    }

    try {
      setLoading(true);
      const profilePicToSave = profilePicRemoved
        ? new Uint8Array(0)
        : (imageArray ?? existingProfilePicBytes ?? undefined);
      await MU.setUserDetails(
        firstName || "",
        lastName || "",
        phoneNumber || "",
        address || "",
        "",
        profilePicToSave
      );

      sessionStorage.removeItem("userDetails");

      setEditProfile(false);
      if (imageArray && profilePicName) {
        await MU.setProfilePic(imageArray, profilePicName);
      }
      setLoading(false);
      // Force refresh to get latest data from backend
      await getUserDetails(true);
      setProfilePicRemoved(false);
      onProfileUpdated?.();
    } catch (error) {
      setLoading(false);
      showToast("Failed to save user details", "error");
      console.error("Failed to save user details:", error);
    }
  };

  return (
    <div className={`General ${editProfile ? "GeneralEdit" : ""}`}>
      {editProfile ? (
        <div className="GeneralProfileEdit">
          <div className="GeneralProfileEditLeft">
            <div className="ProfilePhoto">
              <div className="photo">
                {profilePic ? (
                  <img
                    src={profilePic}
                    alt="Uploaded"
                    className="uploaded-img"
                  />
                ) : (
                  <p>{mailInitial}</p>
                )}
              </div>
              <div className="upload">
                <input
                  type="file"
                  className="fileUpload"
                  ref={uploadRef}
                  accept="image/png, image/jpeg, image/heic"
                  onChange={handleImageUpload}
                  hidden
                />
                {!profilePic ? (
                  <h1 style={{ cursor: "pointer" }} onClick={uploadHandler}>
                    Upload picture
                  </h1>
                ) : (
                  <div className="options">
                    <p className="update" onClick={uploadHandler}>
                      Update
                    </p>
                    <p className="line"></p>
                    <p className="remove" onClick={() => {
                      setprofilePic("");
                      setImageArray(null);
                      setProfilePicRemoved(true);
                    }}>
                      Remove
                    </p>
                  </div>
                )}
                <p className="format">JPG, PNG, or HEIC, Max size of 5 MB</p>
              </div>
            </div>

            <div className="ProfileDetails">
              {/* <div className="CompanyName">
                <p>Name</p>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  maxLength={70}
                />
              </div> */}
              {/* <div className="CompanyName">
                <p>Username</p>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div> */}
              <div className="CompanyName">
                <p>First Name</p>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(
                      /[^a-zA-Z\s]/g,
                      ""
                    );
                    setFirstName(onlyLetters);
                  }}
                  maxLength={50}
                />
              </div>
              <div className="CompanyName">
                <p>Last Name</p>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(
                      /[^a-zA-Z\s]/g,
                      ""
                    );
                    setLastName(onlyLetters);
                  }}
                  maxLength={50}
                />
              </div>
              <div className="CompanyName">
                <p>Phone Number</p>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    style={{
                      fontSize: "14px",
                      padding: "6px 8px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      // background: "#f9f9f9",
                      width: "70px",
                      appearance: "none", // Removes default dropdown arrow styling
                      textAlign: "center",
                      cursor: "pointer"
                    }}
                  >
                    <option value="+91">+91 (IN)</option>
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+61">+61 (AU)</option>
                    <option value="+81">+81 (JP)</option>
                  </select>
                  <input
                    type="text"
                    value={phoneNumber.includes("-") ? phoneNumber.split("-")[1] : phoneNumber}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value.replace(/\D/g, "");
                      setPhoneNumber(onlyNumbers);
                    }}
                    maxLength={15}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              <div className="CompanyName">
                <p>Address</p>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  maxLength={70}
                />
              </div>
            </div>
          </div>

          <div className="GeneralProfileEditRight">
            <Button type="white" title="Cancel" onClick={handleCancel} />
            <Button
              loading={loading}
              disabled={!canSaveProfile}
              type="violet"
              title="Save"
              onClick={handleSave}
            />
          </div>
        </div>
      ) : (
        <div className="GeneralProfile">
          <div className="GeneralDetails">
            <div className="GeneralPhoto">
              {profilePic ? (
                <img src={profilePic} alt="Profile" className="uploadedPhoto" />
              ) : (
                <p>{mailInitial}</p>
              )}
            </div>
            <div className="GeneralPhotoText">
              <div className="PhotoText">
                <p>Name</p>
                <h1>{companyName}</h1>
              </div>
              <div className="PhotoText">
                <p>First Name</p>
                <h1>{firstName}</h1>
              </div>
              <div className="PhotoText">
                <p>Last Name</p>
                <h1>{lastName}</h1>
              </div>
              <div className="PhotoText">
                <p>Phone Number</p>
                <h1>{phoneNumber}</h1>
              </div>
              <div className="PhotoText">
                <p>Address</p>
                <h1>{address}</h1>
              </div>
            </div>
          </div>

          <div className="GeneralEdit" onClick={handleEdit}>
            <img src={theme === "light" ? edit : editDark} alt="edit" />
          </div>
        </div>
      )}

      <Email title="Email" />
    </div>
  );
}

export default EmployeeGeneral;
